import { Input } from "@vds/inputs";
import { useDispatch, useSelector } from "react-redux";
import styles from "./CameraInfoForm.module.css";
import {
  RTSP_VALIDATION_CONSTANTS,
  validateRtspUrl,
  validateCredentialsMatch,
} from "../../../../constants/rtspValidationConstants";

import { useEffect, useState } from "react";
import NumberInput from "../../../../global/components/NumberInput";
import {
  addRtspUrl,
  disableNextButton,
  selectEncodes,
  setSelectedCameraName,
  setHostName,
  setPassword,
  setPortName,
  setUserName,
  setValid,
} from "../../../../store/actions";
import CompanionSelection from "../../commonComponents/CompanionSelection";
import { Body } from "@vds/typography";
import { isDuplicateCamName } from "../../../../global/utility/Utils";
import { DUPLICATE_CAM } from "../../../../pages/CameraOnboarding/cameraOnboardingConstants";



const CameraInfoForm = ({ onNextDisableChange }) => {
  const dispatch = useDispatch();
  const {
    cameraName,
    isValidated,
    userFullName,
    userPassword,
    userHostName,
    userPortName,
    rtspUrl,
    streamInfo,
    selectedCamQR,
  } = useSelector((state) => state.cameraOnboardingReducer);
  const { cameraList } = useSelector((state) => state.deviceReducer);
  const [isValid, setIsValid] = useState(false);
  const [isError, setIsError] = useState();
  const [camera, setCamera] = useState(null);
  const [uname, setUname] = useState("");
  const [pass, setPass] = useState("");
  const [host, setHost] = useState("");
  const [port, setPort] = useState("");
  const [isHostDirty, setIsHostDirty] = useState(false);
  const [isDuplCamName, setIsDuplCamName] = useState(false);
  const isPortError = isError && !!userPortName;
  const validateIP = (value) => RTSP_VALIDATION_CONSTANTS.IP_PATTERN.test(value);
  const validateDomain = (value) => RTSP_VALIDATION_CONSTANTS.DOMAIN_PATTERN.test(value);

  // Validate initial userHostName (e.g., in edit flow)
  useEffect(() => {
    const valid = !!userHostName && (validateIP(userHostName) || validateDomain(userHostName));
    setIsValid(valid);
    dispatch(setValid(valid));
    // Don't set dirty on initial load with valid value
    if (valid) {
      setIsHostDirty(false);
    }
  }, [userHostName, dispatch]);

  const extractPathFromUrl = (url) => {
    if (!url) return "";

    const parts = url.split("://");
    if (parts?.length < 2) return "";

    const urlParts = parts[1].split("/");
    if (urlParts?.length < 2) return "";

    const path = "/" + urlParts?.slice(1)?.join("/");
    return path;
  };

  const buildRtspUrlForStream = (
    username,
    password,
    host,
    port,
    originalUrl
  ) => {
    const existingPath = extractPathFromUrl(originalUrl);
    const encodedUsername = encodeURIComponent(username);
    const encodedPassword = encodeURIComponent(password);
    if(!port || port == ""){
      return `rtsp://${encodedUsername}:${encodedPassword}@${host}${existingPath}`;
    }else{
    return `rtsp://${encodedUsername}:${encodedPassword}@${host}:${port}${existingPath}`;
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    const value = e.target.value;
    const valid = validateIP(value) || validateDomain(value);
    setIsValid(valid);
    dispatch(setValid(valid));
    setHost(value);
    setIsHostDirty(true);
  };

  useEffect(() => {
    if (!isValid || isPortError || isDuplCamName) {
      dispatch(disableNextButton(true));
    } else {
      dispatch(disableNextButton(false));
    }
  }, [isValid, isPortError, isDuplCamName]);

  const handleRtspUrlUpdate = (newUsername, newPassword, newHost, newPort) => {
    const username = newUsername || userFullName;
    const password = newPassword || userPassword;
    const hostname = newHost || userHostName;
    const portname = newPort ?? userPortName;

    if (username && password && hostname && 
        streamInfo && streamInfo.length > 0 && 
        streamInfo.some(stream => stream?.urlRtsp && stream.urlRtsp !== "")) {
      const updatedStreamInfo = streamInfo.map((stream) => ({
        ...stream,
        urlRtsp: stream?.urlRtsp ? buildRtspUrlForStream(
          username,
          password,
          hostname,
          portname,
          stream?.urlRtsp
        ) : "",
      }));
      dispatch(selectEncodes(updatedStreamInfo));

      // Also update the main rtspUrl (for backward compatibility with first stream)
      if (streamInfo?.[0]?.urlRtsp) {
        const mainRtspUrl = buildRtspUrlForStream(
          username,
          password,
          hostname,
          portname,
          streamInfo[0].urlRtsp
        );
        dispatch(addRtspUrl(mainRtspUrl));
      }
    }
  };

  const handleCameraNameChange = (e) => {
    const newValue = e.target.value;
    setCamera(newValue);
    const isDuplicate = isDuplicateCamName(cameraList, selectedCamQR, newValue);
    setIsDuplCamName(isDuplicate);
    dispatch(disableNextButton(isDuplicate));
    dispatch(setSelectedCameraName(newValue));
  };

  return (
    <>
      <div className={styles.root}>
        <div className={styles.fields}>
          <CompanionSelection
            isPairing={true}
            editFlow={false}
            onBoradType="Manual"
          />
          <div className={styles.cameraInfoLabel}>
            <Body
              color="#000000"
              size="large"
              viewport="desktop"
              primitive="span"
            >
              <strong>Camera Info</strong>
            </Body>
          </div>
          <div className={styles.cameraNameField}>
            <Input
              type="text"
              label="Camera name"
              placeholder=""
              required={true}
              width="100%"
              helperText="How the camera will appear with in the 5G Video Insights portal"
              helperTextPlacement="bottom"
              defaultValue={cameraName}
              error={isDuplCamName}
              errorText={isDuplCamName ? DUPLICATE_CAM : ""}
              onChange={handleCameraNameChange}
            />
          </div>
          <div className={styles.credentialsRow}>
            <Input
              type="text"
              label="Username"
              placeholder=""
              required={true}
              width="50%"
              value={userFullName}
              onChange={(e) => {
                const newValue = e.target.value;
                setUname(newValue);
                dispatch(setUserName(newValue));
                handleRtspUrlUpdate(
                  newValue,
                  userPassword,
                  userHostName,
                  userPortName
                );
              }}
            />
            <Input
              type="password"
              label="Password"
              placeholder=""
              value={userPassword}
              required={true}
              width="50%"
              onChange={(e) => {
                const newValue = e.target.value;
                setPass(newValue);
                dispatch(setPassword(newValue));
                handleRtspUrlUpdate(
                  userFullName,
                  newValue,
                  userHostName,
                  userPortName
                );
              }}
            />
          </div>
          <div className={styles.fieldMt}>
            <Input
              type="text"
              label="Camera IP Address / Domain"
              placeholder=""
              required={true}
              width="100%"
              error={isHostDirty && !isValid}
              helperText="Examples: 192.168.1.100, 10.0.0.1, camera.example.com"
              helperTextPlacement="bottom"
              errorText={isHostDirty && !isValid ? "Please enter a valid IP address or domain name" : ""}
              value={userHostName}
              onChange={(e) => {
                const newValue = e.target.value;
                handleChange(e);
                dispatch(setHostName(newValue));
                handleRtspUrlUpdate(
                  userFullName,
                  userPassword,
                  newValue,
                  userPortName
                );
              }}
            />
          </div>
          <div className={styles.fieldMt}>
            <NumberInput
              type="text"
              label="RTSP Port Number"
              isError={isPortError}
              setIsError={setIsError}
              placeholder=""
              min="0"
              max="64000"
              required={false}
              width="100%"
              helperText="Examples: 554, 1024, 8554"
              helperBottom="Examples: 554, 1024, 8554"
              helperTextPlacement="bottom"
              value={userPortName}
              onChange={(value) => {
                setPort(value);
                dispatch(setPortName(value));
                handleRtspUrlUpdate(
                  userFullName,
                  userPassword,
                  userHostName,
                  value
                );
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default CameraInfoForm;
